export const TOLERANCES = {
  shoulderTiltDegMax: 15,
  torsoCenterOffsetPctMax: 10,      // percent of frame width
  bodyHeightFillMin: 0.60,
  bodyHeightFillMax: 0.95,
  sideTurnOffsetPctMax: 15,          // shoulder horizontal spread in side poses
  sideTorsoTiltDegMax: 15,
  visibilityMin: 0.2,                // landmark is considered "detected"
  faceVisibilityMin: 0.35,
};

const POSE_ALIGN_FRONT = new Set(['front', 'front_flex']);
const POSE_ALIGN_BACK  = new Set(['back', 'back_flex']);
const POSE_ALIGN_SIDE  = new Set(['side', 'side_flex']);

function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, visibility: Math.min(a.visibility, b.visibility) };
}

function angleDeg(dx, dy) {
  return Math.atan2(dy, dx) * 180 / Math.PI;
}

function detected(kp) {
  if (!kp) return false;
  // @mediapipe/tasks-vision returns NormalizedLandmark objects with only
  // {x,y,z} for the `pose_landmarker_full` model — the `visibility` field
  // documented in the type definition is not actually populated. When it is
  // (legacy package, certain configurations, or test fixtures) we honor the
  // threshold; when it isn't, we fall back to "is this landmark inside the
  // image frame?". MediaPipe extrapolates coordinates for occluded/offscreen
  // landmarks, so out-of-[0,1] reliably means not visible.
  if (typeof kp.visibility === 'number') return kp.visibility >= TOLERANCES.visibilityMin;
  return kp.x >= 0 && kp.x <= 1 && kp.y >= 0 && kp.y <= 1;
}

function detectedFace(kp) {
  if (!kp) return false;
  if (typeof kp.visibility === 'number') return kp.visibility >= TOLERANCES.faceVisibilityMin;
  // Same fallback as `detected()` but with no separate face threshold available
  // when visibility is missing — being in-frame is the strongest signal we have.
  return kp.x >= 0 && kp.x <= 1 && kp.y >= 0 && kp.y <= 1;
}

// Validate the keypoints look rightside-up. Returns null if sane, an error reason if not.
function checkOrientation(kp) {
  const ls = kp[11], rs = kp[12], lh = kp[23], rh = kp[24];
  if (!detected(ls) || !detected(rs) || !detected(lh) || !detected(rh)) return null;
  const shoulderY = (ls.y + rs.y) / 2;
  const hipY = (lh.y + rh.y) / 2;
  if (shoulderY >= hipY) return 'Photo may be rotated — please retake';
  return null;
}

function gradeFrontOrBack(kp, poseKey, requireFace) {
  const orient = checkOrientation(kp);
  if (orient) return { quality: 'fail', reason: orient };

  const ls = kp[11], rs = kp[12];
  if (!detected(ls) || !detected(rs)) {
    return { quality: 'fail', reason: "We couldn't find your shoulders in frame" };
  }

  if (requireFace && !detectedFace(kp[0])) {
    return { quality: 'warn', reason: 'Face not visible' };
  }

  // Shoulder tilt — measured as deviation from horizontal. We take abs(dx)
  // because MediaPipe labels landmarks from the subject's perspective: in a
  // FRONT pose the subject's left shoulder (kp[11]) is mirrored to the
  // viewer's right (higher image x), so rs.x - ls.x is negative even when
  // shoulders are perfectly level. abs(dx) collapses both orderings to the
  // same horizontal-reference angle.
  const tiltDeg = Math.abs(angleDeg(Math.abs(rs.x - ls.x), rs.y - ls.y));
  if (tiltDeg > TOLERANCES.shoulderTiltDegMax) {
    return { quality: 'warn', reason: 'Shoulders tilted — level up' };
  }

  // Horizontal center offset (torso midpoint vs. frame center 0.5)
  const lh = kp[23], rh = kp[24];
  const anchorX = detected(lh) && detected(rh)
    ? (ls.x + rs.x + lh.x + rh.x) / 4
    : (ls.x + rs.x) / 2;
  const offsetPct = (anchorX - 0.5) * 100;
  if (Math.abs(offsetPct) > TOLERANCES.torsoCenterOffsetPctMax) {
    return { quality: 'warn', reason: offsetPct > 0 ? 'Step left' : 'Step right' };
  }

  // Body height fill — use top of head proxy (nose y - small offset) to ankle midpoint
  const la = kp[27], ra = kp[28];
  const headY = kp[0] && kp[0].visibility >= TOLERANCES.faceVisibilityMin
    ? kp[0].y - 0.05
    : Math.min(ls.y, rs.y) - 0.05;
  const footY = detected(la) && detected(ra)
    ? (la.y + ra.y) / 2
    : detected(lh) && detected(rh) ? (lh.y + rh.y) / 2 + 0.30 : null;
  if (footY != null) {
    const fill = footY - headY;
    if (fill < TOLERANCES.bodyHeightFillMin) {
      return { quality: 'warn', reason: 'Move closer' };
    }
    if (fill > TOLERANCES.bodyHeightFillMax) {
      return { quality: 'warn', reason: 'Step back' };
    }
  }

  return { quality: 'good', reason: null };
}

function gradeSide(kp, poseKey) {
  const orient = checkOrientation(kp);
  if (orient) return { quality: 'fail', reason: orient };

  const ls = kp[11], rs = kp[12];
  if (!detected(ls) || !detected(rs)) {
    return { quality: 'fail', reason: "We couldn't find your shoulders in frame" };
  }

  // In a true side view, shoulders stack nearly on top of each other — small horizontal offset.
  const shoulderSpreadPct = Math.abs(ls.x - rs.x) * 100;
  if (shoulderSpreadPct > TOLERANCES.sideTurnOffsetPctMax) {
    return { quality: 'warn', reason: 'Turn ~15° more' };
  }

  // Torso axis (shoulder mid → hip mid) near-vertical
  const lh = kp[23], rh = kp[24];
  if (detected(lh) && detected(rh)) {
    const sMid = midpoint(ls, rs);
    const hMid = midpoint(lh, rh);
    const tiltFromVerticalDeg = Math.abs(angleDeg(hMid.x - sMid.x, hMid.y - sMid.y) - 90);
    if (tiltFromVerticalDeg > TOLERANCES.sideTorsoTiltDegMax) {
      return { quality: 'warn', reason: 'Body tilted' };
    }
  }

  return { quality: 'good', reason: null };
}

// ── Pose families ──────────────────────────────────────────────────────────
// referencePair() below resolves a pose to landmarks, and different poses
// resolve to DIFFERENT landmarks: frontal poses use the shoulder pair, lateral
// poses use the torso axis. Aligning a shoulder pair against a torso axis is
// meaningless, so a pair whose two sides sit in different families must not
// offer auto-match. 'relaxed' is free-form and is only ever matched to itself.
const POSE_FAMILIES = {
  front: 'frontal', front_flex: 'frontal',
  back: 'frontal', back_flex: 'frontal',
  side: 'lateral', side_flex: 'lateral',
  relaxed: 'free',
};

export function poseFamily(poseKey) {
  return POSE_FAMILIES[poseKey] || null;
}

export function canAutoMatch(poseA, poseB) {
  const fa = poseFamily(poseA);
  return fa !== null && fa === poseFamily(poseB);
}

// ── Reference landmarks per pose (used for transform + canonical frame) ─────
function referencePair(keypoints, poseKey) {
  // Returns { a, b } — two anchor points in normalized coords.
  //  a = primary anchor (centroid of transform),
  //  b = secondary anchor (for scale + rotation).
  if (!keypoints) return null;
  const ls = keypoints[11], rs = keypoints[12];
  const lh = keypoints[23], rh = keypoints[24];

  if (poseKey === 'front' || poseKey === 'front_flex'
   || poseKey === 'back'  || poseKey === 'back_flex') {
    if (!detected(ls) || !detected(rs)) return null;
    // Use left→right shoulder pair so scale reflects shoulder width directly.
    return { a: ls, b: rs };
  }

  if (poseKey === 'side' || poseKey === 'side_flex') {
    // Torso axis: shoulder-mid → hip-mid. Fallback to ear→ankle if torso not detected.
    if (detected(ls) && detected(rs) && detected(lh) && detected(rh)) {
      return { a: midpoint(ls, rs), b: midpoint(lh, rh) };
    }
    const ear = detected(keypoints[7]) ? keypoints[7] : keypoints[8];
    const ank = detected(keypoints[27]) ? keypoints[27] : keypoints[28];
    if (detected(ear) && detected(ank)) return { a: ear, b: ank };
    return null;
  }

  // 'relaxed' = favorite pose — use whatever we can detect; if shoulders missing,
  // fall back to hips only. Alignment is best-effort for this free-form pose.
  if (poseKey === 'relaxed') {
    if (detected(ls) && detected(rs)) return { a: ls, b: rs };
    if (detected(lh) && detected(rh)) return { a: lh, b: rh };
    return null;
  }
  return null;
}

// Compose transform to map `before`'s reference pair onto `after`'s.
// Returns { scale, translateX, translateY, rotate } — all in NORMALIZED units
// (translate in fractions of frame, rotate in degrees).
export function computeAlignment(before, after, poseKey, afterPoseKey = poseKey) {
  if (!before || !after) return null;
  const bp = referencePair(before, poseKey);
  const ap = referencePair(after, afterPoseKey);
  if (!bp || !ap) return null;

  const bDx = bp.b.x - bp.a.x, bDy = bp.b.y - bp.a.y;
  const aDx = ap.b.x - ap.a.x, aDy = ap.b.y - ap.a.y;
  const bLen = Math.hypot(bDx, bDy) || 1e-6;
  const aLen = Math.hypot(aDx, aDy) || 1e-6;

  const scale = aLen / bLen;
  const bAngle = angleDeg(bDx, bDy);
  const aAngle = angleDeg(aDx, aDy);
  const rotate = aAngle - bAngle;

  const translateX = ap.a.x - bp.a.x;
  const translateY = ap.a.y - bp.a.y;

  return { scale, translateX, translateY, rotate };
}

// ── Head anchoring ─────────────────────────────────────────────────────────
// MediaPipe has no crown/top-of-skull landmark — the highest thing it gives us
// is the face cluster (nose, eyes, ears). We estimate the top of the head by
// stepping up from the nose by a fraction of a body dimension that IS
// measurable.
//
// The measure must be VERTICAL. MediaPipe normalizes x by image width and y by
// image height independently, so on a portrait photo a horizontal length (e.g.
// shoulder width) is not comparable to a vertical one — using it here
// overshoots by the aspect ratio, ~40% on a 3:4 photo. The nose→shoulder-line
// gap is purely vertical, so it needs no aspect correction and works in side
// poses too (the head is above the shoulders whichever way the subject faces).
//
// Adult proportions: crown→nose ≈ 0.6 × head height and nose→shoulder-line
// ≈ 1.17 × head height, so crown→nose ≈ 0.51 × the nose→shoulder gap.
// Torso length (shoulder→hip, vertical component) backs it up when the face
// isn't detected: head height ≈ 0.5 × torso.
//
// Absolute accuracy matters less than CONSISTENCY: the same estimate is
// applied to both photos of the same person, so any residual bias cancels and
// the two crowns still land on the same panel line.
const CROWN_PER_NOSE_SHOULDER_GAP = 0.51;
const CROWN_ABOVE_SHOULDER_PER_TORSO = 0.37; // no face: shoulder line → crown
const CROWN_ABOVE_NOSE_FALLBACK = 0.06;      // nose only, nothing to scale by
const MIN_NOSE_SHOULDER_GAP = 0.02;          // below this the detection is junk

// Estimated y of the top of the head, or null when nothing usable is detected.
export function estimateCrownY(keypoints) {
  if (!keypoints) return null;
  const nose = keypoints[0];
  const ls = keypoints[11], rs = keypoints[12];
  const lh = keypoints[23], rh = keypoints[24];

  const hasShoulders = detected(ls) && detected(rs);
  const shoulderY = hasShoulders ? (ls.y + rs.y) / 2 : null;

  if (detected(nose)) {
    if (shoulderY != null) {
      const gap = shoulderY - nose.y;
      if (gap >= MIN_NOSE_SHOULDER_GAP) return nose.y - gap * CROWN_PER_NOSE_SHOULDER_GAP;
    }
    return nose.y - CROWN_ABOVE_NOSE_FALLBACK;
  }

  if (hasShoulders) {
    // No face: step up from the shoulder line by roughly three quarters of a
    // head, sized from the torso's vertical extent.
    const torsoY = (detected(lh) && detected(rh))
      ? Math.abs(midpoint(lh, rh).y - shoulderY)
      : null;
    const drop = torsoY != null ? torsoY * CROWN_ABOVE_SHOULDER_PER_TORSO : 0.12;
    return Math.min(ls.y, rs.y) - drop;
  }
  return null;
}

// Panel position the head anchor is driven to, in percent of the panel box.
// Both photos use the same target, which is what makes the two heads line up.
export const TARGET_HEAD_X_PCT = 50; // horizontal: dead center
export const TARGET_HEAD_Y_PCT = 6;  // vertical: crown near the top of the panel
// Cap on how far a photo is shifted to place the head — past this the body
// gets badly cropped, which matters more than perfect head placement.
export const MAX_HEAD_SHIFT_PCT = 20;

// Resolve a canonical frame into the concrete render geometry for a panel.
// Shared by the renderer (canonicalImgStyle) and by autoMatch, so both agree
// on exactly where a photo's head ends up on screen.
//
// Returns percentages of the panel box: the transform origin (cxPct/cyPct),
// the canonical fill `scale`, the anchoring shift (dxPct/dyPct), the head's
// offset from the origin pre-transform, and where the head finally lands.
export function computeCanonicalRender(canonical) {
  // Every field is defaulted — a partial frame must never yield NaN geometry,
  // which would silently blank the transform on a photo with no keypoints.
  const c = canonical || {};
  const x = c.x ?? 0, y = c.y ?? 0;
  const width = c.width ?? 1, height = c.height ?? 1;
  const cxPct = (x + width / 2) * 100;
  const cyPct = (y + height / 2) * 100;
  const scale = Math.min(1 / Math.max(width, 0.01), 1 / Math.max(height, 0.01));

  const headXPct = (c.headX ?? 0.5) * 100;
  const headYPct = (c.headY ?? 0.08) * 100;
  const headOffsetXPct = headXPct - cxPct;
  const headOffsetYPct = headYPct - cyPct;

  // Where the head sits after the canonical scale (which pivots on the origin).
  const landedX = cxPct + scale * headOffsetXPct;
  const landedY = cyPct + scale * headOffsetYPct;

  const cap = (v) => Math.max(Math.min(v, MAX_HEAD_SHIFT_PCT), -MAX_HEAD_SHIFT_PCT);
  const dxPct = cap(TARGET_HEAD_X_PCT - landedX);
  const dyPct = cap(TARGET_HEAD_Y_PCT - landedY);

  return {
    cxPct, cyPct, scale, dxPct, dyPct,
    headOffsetXPct, headOffsetYPct,
    finalHeadXPct: landedX + dxPct,
    finalHeadYPct: landedY + dyPct,
  };
}

// Per-photo canonical frame = this photo's own body bounding box, padded 8%.
// Use this when each photo should fill its panel independently (best size-match
// between before/after because neither is dominated by the other's larger body).
//
// Also returns `headX` / `headY` — the estimated TOP OF THE HEAD (see
// estimateCrownY; nose / shoulder-line fallbacks) — so the renderer can lock
// the head to a fixed panel position. Without this, heads drift across photos
// because the bbox center is the rendering anchor and bodies of different
// proportions put the head at slightly different fractions-from-center.
export function computePhotoCanonical(keypoints) {
  const FALLBACK = { x: 0, y: 0, width: 1, height: 1, headX: 0.5, headY: 0.08 };
  if (!keypoints) return FALLBACK;
  const xs = [], ys = [];
  for (const p of keypoints) {
    if (detected(p)) { xs.push(p.x); ys.push(p.y); }
  }
  if (xs.length < 4) return FALLBACK;
  const xMin = Math.max(0, Math.min(...xs) - 0.08);
  const xMax = Math.min(1, Math.max(...xs) + 0.08);
  const yMin = Math.max(0, Math.min(...ys) - 0.08);
  const yMax = Math.min(1, Math.max(...ys) + 0.08);

  // Head anchor: the estimated crown (top of head), falling back to the top of
  // the bounding box when no face/shoulders are detectable.
  const nose = keypoints[0];
  const ls = keypoints[11], rs = keypoints[12];
  const crownY = estimateCrownY(keypoints);
  const headY = crownY != null ? crownY : yMin;
  // Horizontal head anchor: shoulder midpoint when available — keeps the
  // torso centered horizontally even if the subject's nose is slightly off
  // from the shoulder mid-line (head turn).
  const headX = (detected(ls) && detected(rs)) ? (ls.x + rs.x) / 2
              : detected(nose) ? nose.x
              : (xMin + (xMax - xMin) / 2);

  return { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin, headX, headY };
}

// Canonical frame = union bounding box over both sets of detectable landmarks,
// padded by 8% so the body isn't cropped at the edges. Kept for callers that
// want a shared frame; prefer computePhotoCanonical for size-matching.
export function computeCanonicalFrame(before, after, poseKey) {
  const FALLBACK = { x: 0, y: 0, width: 1, height: 1 };
  if (!before || !after) return FALLBACK;

  const xs = [], ys = [];
  for (const src of [before, after]) {
    for (const p of src) {
      if (detected(p)) { xs.push(p.x); ys.push(p.y); }
    }
  }
  if (xs.length < 4) return FALLBACK;

  const xMin = Math.max(0, Math.min(...xs) - 0.08);
  const xMax = Math.min(1, Math.max(...xs) + 0.08);
  const yMin = Math.max(0, Math.min(...ys) - 0.08);
  const yMax = Math.min(1, Math.max(...ys) + 0.08);
  return { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin };
}

export function gradeQuality(keypoints, poseKey) {
  if (!keypoints || !Array.isArray(keypoints) || keypoints.length < 29) {
    return { quality: 'fail', reason: "Couldn't detect your body — check lighting and framing" };
  }
  // 'relaxed' = favorite pose (no alignment enforced — clients pick any pose they like)
  if (poseKey === 'relaxed') return { quality: 'good', reason: null };
  if (POSE_ALIGN_FRONT.has(poseKey)) return gradeFrontOrBack(keypoints, poseKey, true);
  if (POSE_ALIGN_BACK.has(poseKey))  return gradeFrontOrBack(keypoints, poseKey, false);
  if (POSE_ALIGN_SIDE.has(poseKey))  return gradeSide(keypoints, poseKey);
  return { quality: 'fail', reason: `Unknown pose: ${poseKey}` };
}
